import { useForm } from '@tanstack/react-form';
import { Printer } from 'lucide-react';
import { z } from 'zod';

import type { ListPrintingRequests } from '@virtual-care/client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@virtual-care/shadcn';
import { FormMessage, errorToFormIssue, useAction } from '@virtual-care/ui';

import { PatientInfo } from './patient-info.tsx';
import { PrintingDoctorSelect } from './printing-doctor-select.tsx';

type PrintingRequestRecord = ListPrintingRequests['records'][number];

interface AssignPrintingDoctorDialogProps {
  selectedRequest: PrintingRequestRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const schema = z.object({
  doctorId: z.string().min(1, 'Please select a doctor'),
});

export function AssignPrintingDoctorDialog({
  selectedRequest,
  open,
  onOpenChange,
  onSuccess,
}: AssignPrintingDoctorDialogProps) {
  const assignDoctorMutation = useAction(
    'PATCH /printing-requests/{id}/assign-doctor',
    {
      invalidate: ['GET /printing-requests'],
      onSuccess: () => {
        onOpenChange(false);
        onSuccess?.();
      },
    },
  );

  const form = useForm({
    defaultValues: { doctorId: '' },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async (props) => {
        try {
          await assignDoctorMutation.mutateAsync({
            id: selectedRequest.id,
            doctorId: props.value.doctorId,
          });
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Doctor — Printing Request</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <PatientInfo patient={selectedRequest.patient} />

          <div className="bg-muted/50 space-y-3 rounded-lg p-4">
            <h3 className="flex items-center gap-2 font-medium">
              <Printer className="h-4 w-4" />
              Case Information
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Speciality:</span>
              <span className="text-sm">{selectedRequest.speciality.name}</span>
            </div>
          </div>

          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.Field
              name="doctorId"
              children={(field) => (
                <PrintingDoctorSelect
                  field={field}
                  printingRequestId={selectedRequest.id}
                  label="Available Doctors"
                />
              )}
            />

            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={!canSubmit || isSubmitting}>
                    {isSubmitting ? 'Assigning...' : 'Assign Doctor'}
                  </Button>
                </DialogFooter>
              )}
            />

            <FormMessage errors={form.state.errors} />
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
